import { baseApi } from '@/services/api';
import { 
  AnalyticsEvent, 
  EventAnalytics, 
  DashboardStats, 
  ProjectAnalytics 
} from './analyticsTypes';

export const analyticsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Dashboard Analytics
    getDashboardStats: builder.query<DashboardStats, { period?: string }>({
      query: (params) => ({
        url: '/analytics/dashboard',
        method: 'GET',
        params,
      }),
      providesTags: ['Analytics'],
    }),
    
    // Event Analytics
    getEventAnalytics: builder.query<EventAnalytics, { documentId: string; period?: string }>({
      query: ({ documentId, period }) => ({
        url: `/analytics/events/${documentId}`,
        method: 'GET',
        params: { period },
      }),
      providesTags: ['Analytics'],
    }),
    
    // Project Analytics
    getProjectAnalytics: builder.query<ProjectAnalytics, { project_id: string; period?: string }>({
      query: ({ project_id, period }) => ({
        url: `/analytics/projects/${project_id}`,
        method: 'GET',
        params: { period },
      }),
      providesTags: ['Analytics'],
    }),
    
    // Analytics Events
    trackEvent: builder.mutation<void, Partial<AnalyticsEvent>>({
      query: (event) => ({
        url: '/analytics/track',
        method: 'POST',
        data: event,
      }),
      invalidatesTags: ['AnalyticsEvents'],
    }),
    
    getAnalyticsEvents: builder.query<AnalyticsEvent[], { 
      user_id?: string; 
      event_type?: string; 
      start_date?: string;
      end_date?: string;
      limit?: number;
    }>({
      query: (params) => ({
        url: '/analytics/events',
        method: 'GET',
        params,
      }),
      providesTags: ['AnalyticsEvents'],
    }),
    
    // User Analytics
    getUserAnalytics: builder.query<any, { user_id: string; period?: string }>({
      query: ({ user_id, period }) => ({
        url: `/analytics/users/${user_id}`,
        method: 'GET',
        params: { period },
      }),
      providesTags: ['Analytics'],
    }),
    
    // Revenue Analytics
    getRevenueAnalytics: builder.query<any, { period?: string; group_by?: string }>({
      query: (params) => ({
        url: '/analytics/revenue',
        method: 'GET',
        params,
      }),
      providesTags: ['Analytics'],
    }),
    
    // Export Analytics
    exportAnalytics: builder.mutation<Blob, { 
      type: 'events' | 'bookings' | 'revenue'; 
      format: 'csv' | 'xlsx' | 'pdf';
      start_date?: string;
      end_date?: string;
    }>({
      query: (params) => ({
        url: '/analytics/export',
        method: 'POST',
        data: params,
        responseType: 'blob',
      }),
    }),
  }),
});

export const {
  useGetDashboardStatsQuery,
  useLazyGetDashboardStatsQuery,
  useGetEventAnalyticsQuery,
  useLazyGetEventAnalyticsQuery,
  useGetProjectAnalyticsQuery,
  useLazyGetProjectAnalyticsQuery,
  useTrackEventMutation,
  useGetAnalyticsEventsQuery,
  useGetUserAnalyticsQuery,
  useGetRevenueAnalyticsQuery,
  useExportAnalyticsMutation,
} = analyticsAPI;
